import type { ReactNode, Ref } from "react";
import type { AssignmentIn } from "../api";
import { t } from "../i18n";

// An assignment's fields — its title and key, what to do, when it is due —
// one set for wherever one is written (RFC-043 model 19): the management
// page's drawer, adding or editing one, labels over the fields; and the New
// class dialog, several at once, each numbered for a screen reader.

/** An assignment as a form holds it: its due date as a datetime-local field has it, in the viewer's zone. */
export type AssignmentDraft = { title: string; key: string; text: string; due: string };

export const blankDraft = (): AssignmentDraft => ({ title: "", key: "", text: "", due: "" });

/** An ISO time as a datetime-local input wants it, in the viewer's zone. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A saved assignment as a draft to edit. */
export function draftOf(a: AssignmentIn): AssignmentDraft {
  return { title: a.title, key: a.key ?? "", text: a.text ?? "", due: a.due_at ? toLocalInput(a.due_at) : "" };
}

/** What the relay takes of a draft: an empty key, text or due date left out. */
export function assignmentIn(d: AssignmentDraft): AssignmentIn {
  return {
    title: d.title.trim(),
    key: d.key.trim() || undefined,
    text: d.text.trim() || undefined,
    due_at: d.due ? new Date(d.due).toISOString() : undefined,
  };
}

/**
 * The fields themselves. `n` given, they are the New class dialog's
 * numbered set — the title and key on a line, `extra` (its Remove) at its
 * end, each field named "Title 1" and so on; not, the drawer's, a label over
 * each field and the key's help beside it.
 */
export function AssignmentFields({ draft, onChange, withKey = true, n, titleRef, extra }: {
  draft: AssignmentDraft;
  onChange: (p: Partial<AssignmentDraft>) => void;
  withKey?: boolean;
  n?: number;
  titleRef?: Ref<HTMLInputElement>;
  extra?: ReactNode;
}) {
  const title = (
    <input
      ref={titleRef}
      className={n !== undefined ? "grow" : undefined}
      value={draft.title}
      onChange={(e) => onChange({ title: e.target.value })}
      placeholder={n !== undefined ? t("classes:assignmentTitle") : undefined}
      aria-label={n !== undefined ? `${t("classes:assignmentTitle")} ${n}` : undefined}
      maxLength={200}
    />
  );
  const key = (
    <input
      className="key-input"
      value={draft.key}
      onChange={(e) => onChange({ key: e.target.value })}
      placeholder={n !== undefined ? `a${n}` : "a1"}
      aria-label={n !== undefined ? `${t("classes:assignmentKey")} ${n}` : undefined}
      maxLength={32}
    />
  );
  const text = (
    <textarea
      value={draft.text}
      onChange={(e) => onChange({ text: e.target.value })}
      placeholder={n !== undefined ? t("classes:assignmentText") : undefined}
      aria-label={n !== undefined ? `${t("classes:assignmentText")} ${n}` : undefined}
      rows={n !== undefined ? 2 : 6}
      maxLength={16000}
    />
  );
  const due = (
    <input
      type="datetime-local"
      value={draft.due}
      onChange={(e) => onChange({ due: e.target.value })}
      aria-label={n !== undefined ? `${t("classes:assignmentDue")} ${n}` : undefined}
    />
  );
  if (n !== undefined) {
    return (
      <>
        <div className="row">
          {title}
          {withKey && key}
          {extra}
        </div>
        {text}
        <label className="new-assignment-due small">
          {t("classes:assignmentDue")}
          {due}
        </label>
      </>
    );
  }
  return (
    <>
      {withKey && (
        <label>
          {t("classes:assignmentKey")}
          {key}
          <span className="muted small"> {t("classes:assignmentKeyHelp")}</span>
        </label>
      )}
      <label>
        {t("classes:assignmentTitle")}
        {title}
      </label>
      <label>
        {t("classes:assignmentText")}
        {text}
      </label>
      <label>
        {t("classes:assignmentDue")}
        {due}
      </label>
    </>
  );
}
